import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  makeStyles,
  CircularProgress,
  Button
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: '100%',
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  contactInfo: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

const formatContactButton = (contact) => {
  if (!contact) return '';
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unnamed';
  const phone = contact.phoneNumbers?.length ? contact.phoneNumbers[0].number : null;
  return (
    <div style={{ textAlign: 'left', lineHeight: 1.3 }}>
      <Typography variant="body2" style={{ fontWeight: 600 }}>{name}</Typography>
      {contact.email && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
          {contact.email}
        </Typography>
      )}
      {phone && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
          {phone}
        </Typography>
      )}
    </div>
  );
};
const getJobsiteLabel = (jobsite) => {
  // console.log('if (jobsite.aspirePropertyId) parts.push(jobsite.aspirePropertyId);', jobsite)
  const parts = [];
  if (jobsite.communityName) parts.push(jobsite.communityName);
  if (jobsite.name) parts.push(jobsite.name);
  const addrParts = [];
  if (jobsite.address) addrParts.push(jobsite.address);
  if (jobsite.city) addrParts.push(jobsite.city);
  if (addrParts.length) parts.push(addrParts.join(', '));
  if (jobsite.buildingNumber) parts.push(`Bldg #${jobsite.buildingNumber}`);
  if (jobsite.aspirePropertyId) parts.push(`(Aspire #${jobsite.aspirePropertyId})`);
  return parts.join(' - ') || 'Unnamed Jobsite';
};

const CustomerComplaintForm = ({
  preSelectedClient,
  preSelectedJobsite,
  onSubmit,
  loading,
  url,
}) => {
  const classes = useStyles();

  const [formData, setFormData] = useState({
    clientId: preSelectedClient?._id || '',
    jobsiteId: preSelectedJobsite?._id || '',
    complaintType: '',
    priority: 'normal',
    description: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    preferredContactMethod: 'phone',
  });

  const [allClients, setAllClients] = useState([]);
  const [allJobsites, setAllJobsites] = useState([]);
  const [selectedClient, setSelectedClient] = useState(preSelectedClient || null);
  const [selectedJobsite, setSelectedJobsite] = useState(preSelectedJobsite || null);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [clientContacts, setClientContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  const complaintTypes = [
    'Service Quality',
    'Scheduling Issue',
    'Billing/Invoice',
    'Property Damage',
    'Communication',
    'Staff Behavior',
    'Safety Concern',
    'Other',
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: '#4caf50' },
    { value: 'normal', label: 'Normal', color: '#ff9800' },
    { value: 'high', label: 'High', color: '#f44336' },
    { value: 'urgent', label: 'Urgent', color: '#d32f2f' },
    { value: 'critical', label: 'Critical', color: '#d32f2f' },
  ];

  // Fetch all clients + jobsites on mount
  useEffect(() => {
    fetchComplaintFormData();
  }, []);

  // When preSelectedClient is provided, set contacts if available
  useEffect(() => {
    if (preSelectedClient) {
      setSelectedClient(preSelectedClient);
      setFormData(prev => ({ ...prev, clientId: preSelectedClient._id }));
      if (preSelectedClient.contacts) {
        setClientContacts(preSelectedClient.contacts);
      }
    }
  }, [preSelectedClient]);

  const fetchComplaintFormData = async () => {
    try {
      setLoadingFormData(true);
      const response = await axios.get(`${url}/api/customer/complaintFormData`);
      if (response.data) {
        setAllClients(response.data.clients || []);
        setAllJobsites(response.data.jobsites || []);
        setAllContacts(response.data.contacts || [])
      }
    } catch (error) {
      console.error('Error fetching complaint form data:', error);
    } finally {
      setLoadingFormData(false);
    }
  };

  const fetchClientContacts = async (clientId) => {
    let clientContactsToSet = allContacts.filter(c => (c.client === clientId))
    // j.client === selectedClient._id || j.client?.toString() === selectedClient._id?.toString())
    console.log('got our client contacts', clientContactsToSet)
    if (clientContactsToSet.length) {
      setClientContacts(clientContactsToSet)
    } else {
      setClientContacts([]);
    }
    // try {
    //   const response = await axios.get(`${url}/api/customer/getContacts?id=${clientId}`);
    //   if (response.data) {
    //     const contacts = Array.isArray(response.data) ? response.data : [];
    //     setClientContacts(contacts);
    //     if (contacts.length > 0) {
    //       const primaryContact = contacts[0];
    //       setFormData(prev => ({
    //         ...prev,
    //         contactName: primaryContact.name || primaryContact.firstName || '',
    //         contactPhone: primaryContact.phone || primaryContact.telephone || '',
    //         contactEmail: primaryContact.email || '',
    //       }));
    //     }
    //   }
    // } catch (error) {
    //   console.error('Error fetching client contacts:', error);
    //   setClientContacts([]);
    // }
  };

  // Contacts available for the search: filter by client if one is selected
  const searchableContacts = selectedClient
    ? allContacts.filter(c => c.client === selectedClient._id || c.client?.toString() === selectedClient._id?.toString())
    : allContacts;

  // Build flat phone options from contacts (each contact can have multiple numbers)
  const phoneOptions = searchableContacts.reduce((acc, contact) => {
    if (contact.phoneNumbers?.length) {
      contact.phoneNumbers.forEach(pn => {
        if (pn.number) {
          const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
          acc.push({ number: pn.number, type: pn.type || '', contactName: name, contact });
        }
      });
    }
    return acc;
  }, []);

  // Build flat email options from contacts
  const emailOptions = searchableContacts.reduce((acc, contact) => {
    if (contact.email) {
      const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
      acc.push({ email: contact.email, contactName: name, contact });
    }
    return acc;
  }, []);

  // Compute filtered jobsites: if a client is selected, show only that client's jobsites
  const filteredJobsites = selectedClient
    ? allJobsites.filter(j => j.client === selectedClient._id || j.client?.toString() === selectedClient._id?.toString())
    : allJobsites;

  // Reset all linked selections back to clean slate
  const clearAllSelections = () => {
    setSelectedClient(null);
    setSelectedJobsite(null);
    setSelectedContact(null);
    setClientContacts([]);
    setFormData(prev => ({
      ...prev,
      clientId: '',
      jobsiteId: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
    }));
  };

  // Auto-select jobsite if client has exactly one
  const autoSelectSingleJobsite = (clientId) => {
    const clientJobsites = allJobsites.filter(j => j.client === clientId || j.client?.toString() === clientId?.toString());
    if (clientJobsites.length === 1) {
      setSelectedJobsite(clientJobsites[0]);
      setFormData(prev => ({ ...prev, jobsiteId: clientJobsites[0]._id }));
    }
  };

  const handleClientChange = (event, value) => {
    if (!value) {
      clearAllSelections();
      return;
    }
    setSelectedClient(value);
    setSelectedJobsite(null);
    setFormData(prev => ({
      ...prev,
      clientId: value._id,
      jobsiteId: '',
    }));
    fetchClientContacts(value._id);
    autoSelectSingleJobsite(value._id);
  };

  const handleJobsiteChange = (event, value) => {
    if (!value) {
      clearAllSelections();
      return;
    }
    setSelectedJobsite(value);
    setFormData(prev => ({
      ...prev,
      jobsiteId: value._id,
    }));
    // Auto-select client when a jobsite is selected
    if (value.client && !selectedClient) {
      const matchingClient = allClients.find(c => c._id === value.client || c._id?.toString() === value.client?.toString());
      if (matchingClient) {
        setSelectedClient(matchingClient);
        setFormData(prev => ({
          ...prev,
          clientId: matchingClient._id,
          jobsiteId: value._id,
        }));
        fetchClientContacts(matchingClient._id);
      }
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleContactSelect = (contact) => {
      console.log('[handleContactSelect', contact)
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    const phone = contact.phoneNumbers?.length ? contact.phoneNumbers[0].number : '';
    setSelectedContact(contact);
    const updates = {
      contactName: name || '',
      contactPhone: phone,
      contactEmail: contact.email || '',
    };
    // Auto-select client from contact if none selected yet
    if (contact.client && !selectedClient) {
      console.log('[handleContactSelect - find matching client...')
      const matchingClient = allClients.find(c => c._id === contact.client || c._id?.toString() === contact.client?.toString());
      console.log('[handleContactSelect Matching Client', matchingClient)
      if (matchingClient) {
        setSelectedClient(matchingClient);
        updates.clientId = matchingClient._id;
        fetchClientContacts(matchingClient._id);
        autoSelectSingleJobsite(matchingClient._id);
      }
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleContactNameChange = (event, value) => {
    if (typeof value === 'string') {
      // freeSolo typed value
      setSelectedContact(null);
      setFormData(prev => ({ ...prev, contactName: value }));
    } else if (value && value._id) {
      // Selected a contact from the list
      handleContactSelect(value);
    } else {
      clearAllSelections();
    }
  };

  const handlePhoneChange = (event, value) => {
    if (typeof value === 'string') {
      setFormData(prev => ({ ...prev, contactPhone: value }));
    } else if (value && value.number) {
      // Selected a phone option — also fill name/email from that contact
      handleContactSelect(value.contact);
    } else {
      clearAllSelections();
    }
  };

  const handleEmailChange = (event, value) => {
    if (typeof value === 'string') {
      setFormData(prev => ({ ...prev, contactEmail: value }));
    } else if (value && value.email) {
      // Selected an email option — also fill name/phone from that contact
      handleContactSelect(value.contact);
    } else {
      clearAllSelections();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData);
  };

  const showReadOnly = !!preSelectedJobsite;

  return (
    <form id="complaint-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {showReadOnly ? (
          // Read-only summary when both pre-selected
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong>Client:</strong> {preSelectedClient?.companyName || preSelectedClient?.displayName || `${preSelectedClient?.firstName} ${preSelectedClient?.lastName}`}
            </Typography>
            <Typography variant="body1">
              <strong>Jobsite:</strong> {getJobsiteLabel(preSelectedJobsite)}
            </Typography>
          </Grid>
        ) : (
          <>
            {/* Jobsite Search — always visible (unless pre-selected) */}
            <Grid item xs={12}>
              <Autocomplete
                options={filteredJobsites}
                getOptionLabel={(option) => getJobsiteLabel(option)}
                value={selectedJobsite}
                onChange={handleJobsiteChange}
                disabled={loading || loadingFormData}
                loading={loadingFormData}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Jobsite / Address"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingFormData ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Client Search — hidden when preSelectedClient provided */}
            {!preSelectedClient && (
              <Grid item xs={12}>
                <Autocomplete
                  options={allClients}
                  getOptionLabel={(option) =>
                    option.companyName || `${option.firstName} ${option.lastName}` || option.displayName
                  }
                  value={selectedClient}
                  onChange={handleClientChange}
                  disabled={loading || loadingFormData}
                  loading={loadingFormData}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Client"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingFormData ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Show pre-selected client as read-only */}
            {preSelectedClient && (
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Client:</strong> {preSelectedClient.companyName || preSelectedClient.displayName || `${preSelectedClient.firstName} ${preSelectedClient.lastName}`}
                </Typography>
              </Grid>
            )}
          </>
        )}

        {/* Contact Information Section */}
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Contact Information
          </Typography>
          <div className={classes.contactInfo}>
            {clientContacts.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Quick select from existing contacts:
                  </Typography>
                  {clientContacts.map((contact, index) => (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      onClick={() => handleContactSelect(contact)}
                      style={{
                        marginRight: 8,
                        marginTop: 8,
                        textTransform: 'none',
                        padding: '6px 12px',
                        borderColor: '#ccc',
                      }}
                    >
                      { formatContactButton(contact) }
                    </Button>
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={searchableContacts}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return [option.firstName, option.lastName].filter(Boolean).join(' ') || '';
                  }}
                  renderOption={(option) => {
                    const name = [option.firstName, option.lastName].filter(Boolean).join(' ');
                    const phone = option.phoneNumbers?.length ? option.phoneNumbers[0].number : '';
                    return (
                      <div>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>{name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {[phone, option.email].filter(Boolean).join(' | ')}
                        </Typography>
                      </div>
                    );
                  }}
                  value={selectedContact || formData.contactName || ''}
                  onChange={handleContactNameChange}
                  onInputChange={(e, val, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, contactName: val }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact Name"
                      variant="outlined"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={phoneOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.number || '';
                  }}
                  renderOption={(option) => (
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>{option.number}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {[option.contactName, option.type].filter(Boolean).join(' - ')}
                      </Typography>
                    </div>
                  )}
                  inputValue={formData.contactPhone}
                  onInputChange={(e, val, reason) => {
                    if (reason === 'input' || reason === 'reset') {
                      setFormData(prev => ({ ...prev, contactPhone: val }));
                    }
                  }}
                  onChange={handlePhoneChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Phone Number"
                      variant="outlined"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={emailOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.email || '';
                  }}
                  renderOption={(option) => (
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>{option.email}</Typography>
                      <Typography variant="caption" color="textSecondary">{option.contactName}</Typography>
                    </div>
                  )}
                  inputValue={formData.contactEmail}
                  onInputChange={(e, val, reason) => {
                    if (reason === 'input' || reason === 'reset') {
                      setFormData(prev => ({ ...prev, contactEmail: val }));
                    }
                  }}
                  onChange={handleEmailChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Email"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="body2" color="textSecondary">
                    Preferred Contact Method:
                  </Typography>
                  <RadioGroup
                    row
                    value={formData.preferredContactMethod}
                    onChange={handleInputChange('preferredContactMethod')}
                  >
                    <FormControlLabel value="phone" control={<Radio />} label="Phone" />
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="text" control={<Radio />} label="Text" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </div>
        </Grid>

        {/* Complaint Details Section */}
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Complaint Details
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Complaint Type</InputLabel>
            <Select
              value={formData.complaintType}
              onChange={handleInputChange('complaintType')}
              label="Complaint Type"
              required
            >
              {complaintTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={handleInputChange('priority')}
              label="Priority"
              required
            >
              {priorityLevels.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  <span style={{ color: level.color }}>
                    {level.label}
                  </span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Complaint Description"
            multiline
            rows={6}
            value={formData.description}
            onChange={handleInputChange('description')}
            fullWidth
            variant="outlined"
            required
            placeholder="Please provide detailed information about the complaint..."
          />
        </Grid>
      </Grid>
    </form>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
});

export default connect(mapStateToProps)(CustomerComplaintForm);
